import { FlagIcon } from "@components:flags/FlagIcon";
import { alpha, Box, useTheme } from "@mui/material";
import { SUPPORTED_LANGUAGES } from "@pages:app/document-translation/utils/language";
import { useTranslation } from "react-i18next";

interface LanguageSelectorProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

/**
 * Compact Pills language selector
 * Flag + 2-letter code, tooltip on hover, checkmark badge when selected
 */
export const LanguageSelector = ({
  value,
  onChange,
  disabled = false,
}: LanguageSelectorProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const handleSelect = (code: string) => {
    onChange(code);
  };

  return (
    <Box>
      {/* Label */}
      <Box
        sx={{
          color: "#1D1D1D",
          fontSize: "15px",
          fontWeight: 600,
          marginBottom: "20px",
          textAlign: "left",
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}
      >
        <Box
          component="span"
          sx={{
            width: "18px",
            height: "18px",
            color: theme.palette.primary.main,
          }}
        >
          🌐
        </Box>
        {t("translation.selectTargetLanguage")}
      </Box>

      {/* Pills */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          gap: "8px",
          marginBottom: "24px",
          maxWidth: "480px",
          marginLeft: "auto",
          marginRight: "auto",
        }}
      >
        {SUPPORTED_LANGUAGES.map((lang) => (
          <Box
            key={lang.code}
            onClick={() => !disabled && handleSelect(lang.code)}
            data-fullname={t(`translation.languages.${lang.code}`)}
            sx={{
              background:
                value === lang.code
                  ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)} 0%, ${alpha(theme.palette.primary.main, 0.1)} 100%)`
                  : "linear-gradient(135deg, #ffffff 0%, #fafafa 100%)",
              border:
                value === lang.code
                  ? `3px solid ${theme.palette.primary.main}`
                  : "2px solid #e5e5e5",
              borderRadius: "24px",
              padding: value === lang.code ? "9px 15px" : "10px 16px",
              cursor: disabled ? "not-allowed" : "pointer",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              position: "relative",
              boxShadow:
                value === lang.code
                  ? `0 8px 20px ${alpha(theme.palette.primary.main, 0.2)}`
                  : "0 2px 8px rgba(0, 0, 0, 0.04)",
              opacity: disabled ? 0.6 : 1,
              "&:hover": {
                borderColor: disabled ? undefined : theme.palette.primary.main,
                background: disabled
                  ? undefined
                  : `linear-gradient(135deg, #ffffff 0%, ${alpha(theme.palette.primary.main, 0.06)} 100%)`,
                transform: disabled ? "none" : "translateY(-2px)",
                boxShadow: disabled
                  ? undefined
                  : `0 6px 16px ${alpha(theme.palette.primary.main, 0.12)}`,
              },
              // Tooltip (no arrow - simplified to avoid flash)
              "&::before": {
                content: "attr(data-fullname)",
                position: "absolute",
                bottom: "calc(100% + 8px)",
                left: "50%",
                transform: "translateX(-50%) scale(0.9)",
                background: "#1D1D1D",
                color: "#ffffff",
                padding: "6px 12px",
                borderRadius: "8px",
                fontSize: "12px",
                fontWeight: 500,
                whiteSpace: "nowrap",
                opacity: 0,
                pointerEvents: "none",
                transition: "all 0.2s ease",
                zIndex: 10,
              },
              "&:hover::before": {
                opacity: 1,
                transform: "translateX(-50%) scale(1)",
              },
              // Checkmark badge for selected pill only
              ...(value === lang.code && {
                "&::after": {
                  content: '""',
                  position: "absolute",
                  top: "-4px",
                  right: "-4px",
                  width: "18px",
                  height: "18px",
                  background: theme.palette.primary.main,
                  borderRadius: "50%",
                  border: "2px solid #ffffff",
                  backgroundImage:
                    "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='3'%3E%3Cpolyline points='20 6 9 17 4 12'/%3E%3C/svg%3E\")",
                  backgroundSize: "10px 10px",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                },
              }),
            }}
          >
            {/* Flag */}
            <Box
              component="span"
              sx={{
                display: "flex",
                alignItems: "center",
                lineHeight: 1,
              }}
            >
              <FlagIcon
                code={lang.flagCode}
                title={t(`translation.languages.${lang.code}`)}
                style={{
                  width: "20px",
                  height: "20px",
                  display: "block",
                }}
              />
            </Box>

            {/* 2-letter code */}
            <Box
              component="span"
              sx={{
                color:
                  value === lang.code ? theme.palette.primary.main : "#1D1D1D",
                fontSize: "13px",
                fontWeight: 700,
                letterSpacing: "0.5px",
              }}
            >
              {lang.code.substring(0, 2).toUpperCase()}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};
